import { useEffect, useRef, useState } from 'react';

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  disabled = false
}: {
  label: string;
  options: ReadonlyArray<string>;
  selected: ReadonlyArray<string>;
  onChange: (next: string[]) => void;
  disabled?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  function toggle(value: string): void {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  }

  return (
    <div className={`msel ${open ? 'open' : ''}`} ref={ref}>
      <div
        className="msel-trigger"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        aria-disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        {selected.length === 0 ? (
          <span className="msel-placeholder">Select {label.toLowerCase()}…</span>
        ) : (
          selected.map((s) => (
            <span className="tag" key={s}>
              {s}
              <span
                className="x"
                role="button"
                aria-label={`Remove ${s}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(s);
                }}
              >
                ×
              </span>
            </span>
          ))
        )}
      </div>
      <div className="msel-menu" role="listbox" aria-label={`${label} options`}>
        {options.map((opt) => {
          const picked = selected.includes(opt);
          return (
            <div
              key={opt}
              role="option"
              aria-selected={picked}
              className={`msel-opt ${picked ? 'picked' : ''}`}
              onClick={() => toggle(opt)}
            >
              <span className="box">✓</span>
              {opt}
            </div>
          );
        })}
      </div>
    </div>
  );
}
